import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ApiToken,
  CreatedToken,
  CreatedWebhook,
  TokenScope,
  WebhookEndpoint,
  WebhookEvent,
} from '@grc/types';
import { apiFetch, apiUrl, ApiRequestError } from '../../lib/api.js';
import { useOrg } from '../../lib/auth.js';
import {
  Badge,
  Button,
  Card,
  Dialog,
  EmptyState,
  Field,
  Input,
  LoadingPane,
  PageHeader,
  Table,
  formatDate,
  useToast,
} from '../../components/ui.js';

const SCOPES: Array<{ value: TokenScope; label: string }> = [
  { value: 'read', label: 'Read — GET endpoints' },
  { value: 'write', label: 'Write — create and update content' },
  { value: 'admin', label: 'Admin — members, tokens, billing' },
];

const EVENTS: Array<{ value: WebhookEvent; label: string }> = [
  { value: 'export.completed', label: 'Export completed' },
  { value: 'response.status_changed', label: 'Response status changed' },
  { value: 'comment.created', label: 'Comment created' },
];

export function TokensPage() {
  const { org } = useOrg();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const isAdmin = org.role === 'admin';

  const tokens = useQuery({
    queryKey: ['tokens', org.orgId],
    queryFn: () => apiFetch<ApiToken[]>(`/orgs/${org.orgId}/tokens`),
    enabled: isAdmin,
  });
  const webhooks = useQuery({
    queryKey: ['webhooks', org.orgId],
    queryFn: () => apiFetch<WebhookEndpoint[]>(`/orgs/${org.orgId}/webhooks`),
    enabled: isAdmin,
  });

  const onError = (err: unknown) =>
    toast(err instanceof ApiRequestError ? err.message : 'Action failed', 'error');

  // --- tokens
  const [tokenOpen, setTokenOpen] = useState(false);
  const [tokenName, setTokenName] = useState('');
  const [tokenScopes, setTokenScopes] = useState<TokenScope[]>(['read']);
  const [createdToken, setCreatedToken] = useState<CreatedToken | null>(null);
  const createToken = useMutation({
    mutationFn: () =>
      apiFetch<CreatedToken>(`/orgs/${org.orgId}/tokens`, {
        method: 'POST',
        body: { name: tokenName.trim(), scopes: tokenScopes },
      }),
    onSuccess: (created) => {
      setCreatedToken(created);
      setTokenOpen(false);
      setTokenName('');
      void queryClient.invalidateQueries({ queryKey: ['tokens', org.orgId] });
    },
    onError,
  });
  const revokeToken = useMutation({
    mutationFn: (id: string) => apiFetch(`/tokens/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast('Token revoked.', 'success');
      void queryClient.invalidateQueries({ queryKey: ['tokens', org.orgId] });
    },
    onError,
  });

  // --- webhooks
  const [hookOpen, setHookOpen] = useState(false);
  const [hookUrl, setHookUrl] = useState('');
  const [hookEvents, setHookEvents] = useState<WebhookEvent[]>(['export.completed']);
  const [createdHook, setCreatedHook] = useState<CreatedWebhook | null>(null);
  const createHook = useMutation({
    mutationFn: () =>
      apiFetch<CreatedWebhook>(`/orgs/${org.orgId}/webhooks`, {
        method: 'POST',
        body: { url: hookUrl.trim(), events: hookEvents },
      }),
    onSuccess: (created) => {
      setCreatedHook(created);
      setHookOpen(false);
      setHookUrl('');
      void queryClient.invalidateQueries({ queryKey: ['webhooks', org.orgId] });
    },
    onError,
  });
  const deleteHook = useMutation({
    mutationFn: (id: string) => apiFetch(`/webhooks/${id}`, { method: 'DELETE' }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['webhooks', org.orgId] }),
    onError,
  });

  if (!isAdmin) {
    return <EmptyState title="Admins only" message="API access is managed by organisation admins." />;
  }
  if (tokens.isLoading) return <LoadingPane label="Loading API settings" />;

  const toggle = <T,>(list: T[], value: T): T[] =>
    list.includes(value) ? list.filter((v) => v !== value) : [...list, value];

  const submitToken = (e: FormEvent) => {
    e.preventDefault();
    if (tokenName.trim() && tokenScopes.length) createToken.mutate();
  };

  return (
    <div>
      <PageHeader
        title="API & webhooks"
        subtitle={
          <>
            Integrate with the documented REST API —{' '}
            <a href={apiUrl('/docs')} target="_blank" rel="noreferrer">
              interactive docs
            </a>{' '}
            ·{' '}
            <a href={apiUrl('/openapi.json')} target="_blank" rel="noreferrer">
              OpenAPI spec
            </a>
          </>
        }
      />

      <Card
        title="API tokens"
        actions={
          <Button size="sm" onClick={() => setTokenOpen(true)}>
            Create token
          </Button>
        }
      >
        {(tokens.data ?? []).length === 0 ? (
          <p style={{ color: 'var(--color-text-muted)' }}>
            Tokens are scoped to this organisation and sent as{' '}
            <code>Authorization: Bearer zest_…</code>.
          </p>
        ) : (
          <Table
            head={
              <>
                <th>Name</th>
                <th>Scopes</th>
                <th>Created</th>
                <th>Last used</th>
                <th>Status</th>
                <th />
              </>
            }
          >
            {(tokens.data ?? []).map((t) => (
              <tr key={t.id}>
                <td>{t.name}</td>
                <td>
                  {t.scopes.map((s) => (
                    <Badge key={s} tone="primary">
                      {s}
                    </Badge>
                  ))}
                </td>
                <td>{formatDate(t.createdAt)}</td>
                <td>{formatDate(t.lastUsedAt)}</td>
                <td>{t.revokedAt ? <Badge tone="danger">revoked</Badge> : <Badge tone="success">active</Badge>}</td>
                <td>
                  {!t.revokedAt && (
                    <Button size="sm" variant="secondary" onClick={() => revokeToken.mutate(t.id)}>
                      Revoke
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      <Card
        title="Outbound webhooks"
        actions={
          <Button size="sm" variant="secondary" onClick={() => setHookOpen(true)}>
            Add endpoint
          </Button>
        }
      >
        {(webhooks.data ?? []).length === 0 ? (
          <p style={{ color: 'var(--color-text-muted)' }}>
            Receive signed HTTP callbacks when exports complete, response statuses change or
            comments are added. Payloads carry an <code>X-Zest-Signature</code> HMAC header.
          </p>
        ) : (
          <Table
            head={
              <>
                <th>URL</th>
                <th>Events</th>
                <th>Created</th>
                <th />
              </>
            }
          >
            {(webhooks.data ?? []).map((w) => (
              <tr key={w.id}>
                <td style={{ wordBreak: 'break-all' }}>{w.url}</td>
                <td>
                  {w.events.map((ev) => (
                    <Badge key={ev}>{ev}</Badge>
                  ))}
                </td>
                <td style={{ whiteSpace: 'nowrap' }}>{formatDate(w.createdAt)}</td>
                <td>
                  <Button size="sm" variant="secondary" onClick={() => deleteHook.mutate(w.id)}>
                    Delete
                  </Button>
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      <Dialog open={tokenOpen} onClose={() => setTokenOpen(false)} title="Create API token">
        <form onSubmit={submitToken}>
          <Field label="Name" required hint="What will use this token?">
            {(props) => (
              <Input {...props} required value={tokenName} onChange={(e) => setTokenName(e.target.value)} />
            )}
          </Field>
          <fieldset style={{ border: 'none', padding: 0, margin: '0 0 16px' }}>
            <legend className="field-label">Scopes</legend>
            {SCOPES.map((s) => (
              <label key={s.value} style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                <input
                  type="checkbox"
                  checked={tokenScopes.includes(s.value)}
                  onChange={() => setTokenScopes(toggle(tokenScopes, s.value))}
                />
                {s.label}
              </label>
            ))}
          </fieldset>
          <div className="dialog-actions">
            <Button variant="secondary" onClick={() => setTokenOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" loading={createToken.isPending}>
              Create token
            </Button>
          </div>
        </form>
      </Dialog>

      <Dialog open={!!createdToken} onClose={() => setCreatedToken(null)} title="Token created">
        <p>
          Copy this token now — <strong>it will not be shown again</strong>.
        </p>
        <p>
          <code style={{ wordBreak: 'break-all' }}>{createdToken?.token}</code>
        </p>
        <div className="dialog-actions">
          <Button
            variant="secondary"
            onClick={() => {
              if (createdToken) void navigator.clipboard.writeText(createdToken.token);
              toast('Token copied.', 'success');
            }}
          >
            Copy
          </Button>
          <Button onClick={() => setCreatedToken(null)}>Done</Button>
        </div>
      </Dialog>

      <Dialog open={hookOpen} onClose={() => setHookOpen(false)} title="Add webhook endpoint">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (hookUrl.trim() && hookEvents.length) createHook.mutate();
          }}
        >
          <Field label="Endpoint URL" required>
            {(props) => (
              <Input
                {...props}
                type="url"
                required
                placeholder="https://example.com/webhooks/zest-assure"
                value={hookUrl}
                onChange={(e) => setHookUrl(e.target.value)}
              />
            )}
          </Field>
          <fieldset style={{ border: 'none', padding: 0, margin: '0 0 16px' }}>
            <legend className="field-label">Events</legend>
            {EVENTS.map((ev) => (
              <label key={ev.value} style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                <input
                  type="checkbox"
                  checked={hookEvents.includes(ev.value)}
                  onChange={() => setHookEvents(toggle(hookEvents, ev.value))}
                />
                {ev.label}
              </label>
            ))}
          </fieldset>
          <div className="dialog-actions">
            <Button variant="secondary" onClick={() => setHookOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" loading={createHook.isPending}>
              Add endpoint
            </Button>
          </div>
        </form>
      </Dialog>

      <Dialog open={!!createdHook} onClose={() => setCreatedHook(null)} title="Webhook created">
        <p>
          Signing secret — verify each delivery's <code>X-Zest-Signature</code> header with it.{' '}
          <strong>Shown once.</strong>
        </p>
        <p>
          <code style={{ wordBreak: 'break-all' }}>{createdHook?.secret}</code>
        </p>
        <div className="dialog-actions">
          <Button
            variant="secondary"
            onClick={() => {
              if (createdHook) void navigator.clipboard.writeText(createdHook.secret);
              toast('Secret copied.', 'success');
            }}
          >
            Copy
          </Button>
          <Button onClick={() => setCreatedHook(null)}>Done</Button>
        </div>
      </Dialog>
    </div>
  );
}
